// FASE 3.5 — Add-to-list popover button on profile pages.
import React, { useCallback, useEffect, useState } from "react";
import { ListPlus, Check, Loader2 } from "lucide-react";
import { API, safeJson } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tFases } from "../../i18n/shadow-fases";

export const AddToListButton = ({ targetUsername }) => {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState({});

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    if (!user) return;
    const r = await fetch(`${API}/shadow/lists`, { credentials: "include", headers });
    const d = await safeJson(r);
    if (r.ok && d?.lists) setLists(d.lists);
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) load(); }, [open, load]);

  const addToList = async (listId) => {
    if (busy) return;
    setBusy(listId);
    try {
      const r = await fetch(`${API}/shadow/lists/${listId}/members`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ username: targetUsername.toLowerCase() }),
      });
      if (r.ok) setDone((d) => ({ ...d, [listId]: true }));
    } finally { setBusy(null); }
  };

  if (!user) return null;

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen((o) => !o)}
        title={tFases(lang, "atl_tooltip")}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white hover:border-cyan-400/60 hover:text-cyan-300 transition-all"
        data-testid="profile-add-to-list-btn"
      >
        <ListPlus className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{tFases(lang, "atl_label")}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-xl border border-white/10 bg-zinc-950 shadow-[0_8px_28px_rgba(0,0,0,0.55)]"
          data-testid="add-to-list-popover"
        >
          <div className="border-b border-white/10 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">
            {tFases(lang, "atl_header")}
          </div>
          {lists.length === 0 ? (
            <p className="p-3 text-xs text-white/55">{tFases(lang, "atl_empty")} <a href="/shadow/listas" className="text-cyan-300 hover:underline">{tFases(lang, "atl_create")}</a></p>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {lists.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => addToList(l.id)}
                    disabled={busy === l.id || done[l.id]}
                    data-testid={`add-to-list-${l.id}`}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-white/[0.04]"
                  >
                    <span className="truncate text-white/80">{l.name}</span>
                    {done[l.id] ? <Check className="h-3.5 w-3.5 text-emerald-400" />
                      : busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-white/40" />
                      : <span className="text-[9px] font-mono text-white/40">{l.member_count}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AddToListButton;
